///
/// EarthBrowser
///
/// Created by Matt Giger on 1/13/2025
/// Copyright (c) 2025 Geodata Labs
///

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import type { LinkedAccountRecord } from "@shared-types/user"
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar"
import { Badge } from "@/components/ui/badge"
import { Icons } from "@/components/ui/icons"
import { AppleIcon, LockIcon, BadgeHelp, CheckCircle2, XCircle } from "lucide-react"
import { format } from "date-fns"

interface ViewAccountDialogProps {
	account: LinkedAccountRecord | null
	open: boolean
	onOpenChange: (open: boolean) => void
}

function ProviderIcon({ provider, method }: { provider: LinkedAccountRecord["provider"], method: LinkedAccountRecord["method"] }) {
	switch (provider) {
		case "google":
			return <Icons.googleLogo className="h-6 w-6" />
		case "apple":
			return <AppleIcon className="h-6 w-6" />
		case "github":
			return <Icons.gitHub className="h-6 w-6" />
		default:
			if (method === "password") {
				return <LockIcon className="h-6 w-6" />
			}
			return <BadgeHelp className="h-6 w-6" />
	}
}

function ProviderName({ provider, method }: { provider: LinkedAccountRecord["provider"], method: LinkedAccountRecord["method"] }) {
	switch (provider) {
		case "google":
			return "Google"
		case "apple":
			return "Apple"
		case "github":
			return "GitHub"
		default:
			if (method === "password") {
				return "Password"
			}
			return "Unknown"
	}
}

export function ViewAccountDialog({ account, open, onOpenChange }: ViewAccountDialogProps) {
	if (!account) {
		return null
	}

	const linkedDate = account.created_at ? format(new Date(account.created_at), "PPpp") : "Unknown"

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Account Details</DialogTitle>
					<DialogDescription>
						View information about this linked account
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-6 py-4">
					{/* Account Header */}
					<div className="flex items-center gap-4">
						<Avatar className="h-16 w-16">
							{account.image_url && account.image_url.length > 0 ? (
								<>
									<AvatarImage className="rounded-full" src={account.image_url} alt={account.username} />
									<AvatarFallback className="text-lg">{account.username.charAt(0)}</AvatarFallback>
								</>
							) : (
								<AvatarFallback className="text-lg">{account.username.charAt(0)}</AvatarFallback>
							)}
						</Avatar>
						<div className="flex-1">
							<div className="flex items-center gap-2">
								<ProviderIcon provider={account.provider} method={account.method} />
								<h3 className="text-lg font-semibold">{ProviderName({ provider: account.provider, method: account.method })}</h3>
							</div>
							<p className="text-sm text-muted-foreground">{account.username}</p>
						</div>
					</div>

					{/* Account Details */}
					<div className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="text-sm font-medium text-muted-foreground">Email</label>
								<p className="text-sm font-medium">{account.email || "Not provided"}</p>
							</div>
							<div>
								<label className="text-sm font-medium text-muted-foreground">Name</label>
								<p className="text-sm font-medium">
									{account.first_name && account.last_name
										? `${account.first_name} ${account.last_name}`
										: account.first_name || account.last_name || "Not provided"}
								</p>
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="text-sm font-medium text-muted-foreground">Status</label>
								<div className="flex items-center gap-2 mt-1">
									{account.is_verified ? (
										<Badge variant="default" className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20">
											<CheckCircle2 className="h-3 w-3 mr-1" />
											Verified
										</Badge>
									) : (
										<Badge variant="secondary">
											<XCircle className="h-3 w-3 mr-1" />
											Unverified
										</Badge>
									)}
								</div>
							</div>
							<div>
								<label className="text-sm font-medium text-muted-foreground">Primary Account</label>
								<div className="flex items-center gap-2 mt-1">
									{account.is_primary ? (
										<Badge variant="default" className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20">
											Yes
										</Badge>
									) : (
										<Badge variant="secondary">No</Badge>
									)}
								</div>
							</div>
						</div>

						<div>
							<label className="text-sm font-medium text-muted-foreground">Authentication Method</label>
							<p className="text-sm font-medium capitalize">{account.method}</p>
						</div>

						<div>
							<label className="text-sm font-medium text-muted-foreground">Linked Date</label>
							<p className="text-sm font-medium">{linkedDate}</p>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
